import { changeTilesets, framePlacementsOf, tilesetUses, type TilesetEntry, type TilesetFrames } from "./tileset-change.ts";
import { sortedFrames } from "./tileset-frames.ts";
import type { WorldDocument } from "./types.ts";

/**
 * The slim upgrade (ADR-130 decision 1.4, P1.6-T264): a world saved before partial entries lists whole entries
 * for props tilesets, which would mean listing every frame of them as the world's items. `slimWorld` replaces
 * each by the frames the world places, or removes it when nothing places it.
 */

/**
 * What a tileset is for, as the host's catalogue says: `terrain` holds terrain and tile art, `props` holds
 * placeable frames.
 */
export type TilesetRole = "terrain" | "props";

/**
 * The result of a slim pass.
 */
export interface SlimReport {
  readonly world: WorldDocument;
  readonly removed: readonly string[];
  readonly slimmed: readonly { readonly id: string; readonly frames: readonly number[] }[];
}

/**
 * Reports whether a tileset id is a pack's studio tileset (`<pack>:studio`, ADR-107 decision 2), which holds the
 * sprites of a pack's drafts and is a unit of draft and publish (ADR-130 H6), so a slim never narrows it.
 *
 * @param id The tileset id.
 * @returns True when the id ends `:studio`.
 */
function isStudioTileset(id: string): boolean {
  return id.endsWith(":studio");
}

/**
 * Replaces each whole entry of a props-role tileset by the partial entry of the frames the world places from it
 * (a prop's frame, a state frame, a transition animation: `framePlacementsOf`), and removes the entry when
 * nothing places it, renumbering every tile cell after it through `changeTilesets`. A studio pack's tileset, an entry of any other role,
 * an entry whose role is unknown, an entry already partial and every entry a tile cell or a terrain layer names
 * are left as they are. A pass over a slimmed world changes nothing.
 *
 * @param world The document; never mutated.
 * @param roleOf The host's role of a tileset id, undefined when the host does not know it.
 * @returns The slimmed document, the same object when nothing changed, with the ids removed and the entries
 *   made partial, both in list order.
 * @throws Error when a tile cell indexes past the world's tilesets.
 */
export function slimWorld(world: WorldDocument, roleOf: (tileset: string) => TilesetRole | undefined): SlimReport {
  const candidates = world.tilesets.filter((entry) => entry.frames === undefined && !isStudioTileset(entry.id) && roleOf(entry.id) === "props");
  if (candidates.length === 0) {
    return { world, removed: [], slimmed: [] };
  }
  const named = new Set<string>();
  for (const use of tilesetUses(world)) {
    if (use.kind === "tile" || use.kind === "terrain") {
      named.add(use.tileset);
    }
  }
  const slimmable = new Set(candidates.filter((entry) => !named.has(entry.id)).map((entry) => entry.id));
  const placements = framePlacementsOf(world, slimmable);
  const remove: TilesetEntry[] = [];
  const frames: TilesetFrames[] = [];
  const slimmed: { id: string; frames: number[] }[] = [];
  world.tilesets.forEach((entry, index) => {
    if (!slimmable.has(entry.id)) {
      return;
    }
    const placed = placements.get(entry.id);
    if (placed === undefined) {
      remove.push({ index, id: entry.id });
      return;
    }
    const after = sortedFrames(placed.keys());
    frames.push({ id: entry.id, after });
    slimmed.push({ id: entry.id, frames: after });
  });
  if (remove.length === 0 && frames.length === 0) {
    return { world, removed: [], slimmed: [] };
  }
  return { world: changeTilesets(world, remove, [], frames), removed: remove.map((entry) => entry.id), slimmed };
}
